import { useState, useEffect, useRef, useCallback } from "react";
import {
  XMarkIcon,
  SunIcon,
  MoonIcon,
  PhoneIcon,
} from "@heroicons/react/24/outline";
import { useTheme } from "../context/ThemeContext";

const logo =
  "https://res.cloudinary.com/dbfyzgffj/image/upload/v1758905512/logo_zjf0g4.png";

const navItems = [
  { label: "Home", id: "home" },
  { label: "Performance", id: "performance_support" },
  { label: "Gallery", id: "gallery" },
  { label: "Achievements", id: "achievements" },
  { label: "Branches", id: "branches" },
];

const Header = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [activeId, setActiveId] = useState("home");
  const [indicatorStyle, setIndicatorStyle] = useState({ left: 0, width: 0 });
  const navRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<Map<string, HTMLButtonElement>>(new Map());
  const { theme, toggleTheme } = useTheme();

  const handleScroll = useCallback(() => {
    setScrolled(window.scrollY > 20);

    const sections = navItems
      .map((item) => {
        const el = document.getElementById(item.id);
        if (!el) return null;
        return { id: item.id, top: el.getBoundingClientRect().top };
      })
      .filter(Boolean) as { id: string; top: number }[];

    const current = sections.reduce((acc, sec) => {
      if (sec.top <= 140) return sec.id;
      return acc;
    }, "home");

    setActiveId(current);
  }, []);

  useEffect(() => {
    window.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, [handleScroll]);

  useEffect(() => {
    if (navRef.current) {
      const activeBtn = itemRefs.current.get(activeId);
      if (activeBtn && navRef.current) {
        const navRect = navRef.current.getBoundingClientRect();
        const btnRect = activeBtn.getBoundingClientRect();
        setIndicatorStyle({
          left: btnRect.left - navRect.left,
          width: btnRect.width,
        });
      }
    }
  }, [activeId, scrolled]);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      window.scrollTo({
        top: element.offsetTop - 80,
        behavior: "smooth",
      });
    }
    setIsOpen(false);
  };

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          scrolled
            ? "bg-[var(--bg-primary)]/90 backdrop-blur-xl shadow-[0_1px_20px_rgba(0,0,0,0.08)] border-b border-[var(--header-border)]"
            : "bg-gradient-to-b from-black/50 via-black/20 to-transparent"
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-16 md:h-[84px]">
          {/* Logo */}
          <button
            onClick={() => scrollToSection("home")}
            className="flex items-center gap-3 group flex-shrink-0"
          >
            <div className="relative">
              <img
                src={logo}
                alt="OCSSC Logo"
                className="w-9 h-9 md:w-18 md:h-18 rounded-full transition-all duration-300 group-hover:scale-105"
              />
              <div className="absolute inset-0 rounded-full ring-2 ring-orange-400/0 group-hover:ring-orange-400/60 transition-all duration-300" />
            </div>
            {/* Desktop: full name + affiliation */}
            <div className="hidden sm:flex flex-col items-start leading-none">
              <span
                className={`gowun-batang font-bold text-[22px] transition-colors duration-300 ${
                  scrolled ? "text-[var(--text-primary)]" : "text-white"
                }`}
              >
                Orange City Sport Shooting Club
              </span>
              <span
                className={`text-[11px] font-medium tracking-[0.15em] uppercase mt-2 transition-colors duration-300 ${
                  scrolled ? "text-[var(--text-secondary)]" : "text-white/60"
                }`}
              >
                Affiliated to Maharashtra Rifle Association
              </span>
            </div>

            {/* Mobile: short name + affiliation on separate line */}
            <div className="flex sm:hidden flex-col items-start leading-none -ml-2">
              <span
                className={`gowun-batang font-bold text-[18px] transition-colors duration-300 ${
                  scrolled ? "text-[var(--text-primary)]" : "text-white"
                }`}
              >
                Orange City Sport Shooting Club
              </span>
              <span
                className={`text-[9px] font-medium tracking-[0.16em] uppercase mt-1 transition-colors duration-300 ${
                  scrolled ? "text-[var(--text-secondary)]" : "text-white/80"
                }`}
              >
                Affiliated to Maharashtra Rifle Association
              </span>
            </div>
          </button>

          {/* Desktop Nav */}
          <nav className="hidden lg:flex items-center relative" ref={navRef}>
            {/* Sliding indicator */}
            <div
              className="absolute bottom-0 h-[2px] bg-orange-500 rounded-full transition-all duration-300 ease-out"
              style={{ left: indicatorStyle.left, width: indicatorStyle.width }}
            />

            {navItems.map((item) => (
              <button
                key={item.id}
                ref={(el) => {
                  if (el) itemRefs.current.set(item.id, el);
                }}
                onClick={() => scrollToSection(item.id)}
                className={`relative px-3 xl:px-4 py-2 text-base font-medium transition-colors duration-200 ${
                  activeId === item.id
                    ? scrolled
                      ? "text-orange-600 dark:text-orange-400"
                      : "text-white"
                    : scrolled
                      ? "text-[var(--text-secondary)] hover:text-[var(--text-primary)]"
                      : "text-white/70 hover:text-white"
                }`}
              >
                {item.label}
                {/* Active dot */}
                {activeId === item.id && (
                  <span className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full bg-orange-500 lg:hidden" />
                )}
              </button>
            ))}
          </nav>

          {/* Right side actions */}
          <div className="flex items-center gap-2">
            {/* Theme toggle */}
            {/* <button
              onClick={toggleTheme}
              className={`hidden md:flex p-2.5 rounded-xl transition-all duration-200 ${
                scrolled
                  ? "text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-tertiary)]"
                  : "text-white/70 hover:text-white hover:bg-white/10"
              }`}
              aria-label="Toggle theme"
            >
              {theme === "light" ? (
                <MoonIcon className="w-[18px] h-[18px]" />
              ) : (
                <SunIcon className="w-[18px] h-[18px]" />
              )}
            </button> */}

            {/* CTA Button */}
            {/* <a
              href="tel:+917385035152"
              className={`hidden md:inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold transition-all duration-300 ${
                scrolled
                  ? "bg-orange-500 text-white hover:bg-orange-600 shadow-md shadow-orange-500/20"
                  : "bg-white/15 text-white border border-white/20 hover:bg-white/25 backdrop-blur-sm"
              }`}
            >
              <PhoneIcon className="w-3.5 h-3.5" />
              Contact
            </a> */}

            {/* Mobile hamburger */}
            <button
              onClick={() => setIsOpen(!isOpen)}
              className={`lg:hidden p-2.5 rounded-xl transition-all duration-200 ${
                scrolled
                  ? "text-[var(--text-secondary)] hover:bg-[var(--bg-tertiary)]"
                  : "text-white/80 hover:text-white hover:bg-white/10"
              }`}
              aria-label="Toggle menu"
            >
              {isOpen ? (
                <XMarkIcon className="w-5 h-5" />
              ) : (
                <div className="flex flex-col gap-[5px]">
                  <span className="w-5 h-[1.5px] bg-current rounded-full" />
                  <span className="w-3.5 h-[1.5px] bg-current rounded-full" />
                  <span className="w-5 h-[1.5px] bg-current rounded-full" />
                </div>
              )}
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Menu Overlay */}
      <div
        className={`fixed inset-0 z-40 lg:hidden transition-all duration-400 ${
          isOpen
            ? "opacity-100 pointer-events-auto"
            : "opacity-0 pointer-events-none"
        }`}
      >
        {/* Backdrop */}
        <div
          className="absolute inset-0 bg-black/50 backdrop-blur-sm"
          onClick={() => setIsOpen(false)}
        />

        {/* Panel */}
        <div
          className={`absolute top-0 right-0 h-full w-[300px] max-w-[85vw] bg-[var(--bg-primary)] shadow-2xl transition-transform duration-400 ease-[cubic-bezier(0.16,1,0.3,1)] ${
            isOpen ? "translate-x-0" : "translate-x-full"
          }`}
        >
          <div className="flex flex-col h-full">
            {/* Panel Header */}
            <div className="flex items-center justify-between px-6 h-16 border-b border-[var(--border-light)]">
              <div className="flex items-center gap-2.5"></div>
              <button
                onClick={() => setIsOpen(false)}
                className="p-2 rounded-xl text-[var(--text-secondary)] hover:bg-[var(--bg-tertiary)] transition-all"
                aria-label="Close menu"
              >
                {/* <XMarkIcon className="w-5 h-5" /> */}
              </button>
            </div>

            {/* Nav Items */}
            <div className="flex-1 overflow-y-auto py-6 px-4">
              <nav className="flex flex-col gap-1">
                {navItems.map((item, i) => (
                  <button
                    key={item.id}
                    onClick={() => scrollToSection(item.id)}
                    className={`text-left px-4 py-3.5 rounded-xl font-medium transition-all duration-200 ${
                      activeId === item.id
                        ? "bg-orange-50 dark:bg-orange-900/15 text-orange-600 dark:text-orange-400"
                        : "text-[var(--text-primary)] hover:bg-[var(--bg-tertiary)]"
                    }`}
                    style={{ animationDelay: `${i * 50}ms` }}
                  >
                    <span className="flex items-center gap-3">
                      {activeId === item.id && (
                        <span className="w-1.5 h-1.5 rounded-full bg-orange-500 flex-shrink-0" />
                      )}
                      {item.label}
                    </span>
                  </button>
                ))}
              </nav>

              {/* Divider */}
              <div className="my-6 border-t border-[var(--border-light)]" />

              {/* Theme toggle */}
              {/* <button
                onClick={toggleTheme}
                className="w-full flex items-center gap-3 px-4 py-3.5 rounded-xl text-[var(--text-primary)] hover:bg-[var(--bg-tertiary)] font-medium transition-all"
              >
                <div className="w-8 h-8 rounded-lg bg-[var(--bg-tertiary)] flex items-center justify-center">
                  {theme === "light" ? (
                    <MoonIcon className="w-4 h-4" />
                  ) : (
                    <SunIcon className="w-4 h-4" />
                  )}
                </div>
                {theme === "light" ? "Dark Mode" : "Light Mode"}
              </button> */}
            </div>

            {/* Panel Footer */}
            {/* <div className="px-6 py-5 border-t border-[var(--border-light)]">
              <a
                href="tel:+917385035152"
                className="flex items-center justify-center gap-2.5 w-full py-3 rounded-xl bg-orange-500 hover:bg-orange-600 text-white font-semibold text-sm transition-all duration-200 shadow-md shadow-orange-500/20"
              >
                <PhoneIcon className="w-4 h-4" />
                +91 7385035152
              </a>
            </div> */}
          </div>
        </div>
      </div>
    </>
  );
};

export default Header;
